import { useQuery } from '@apollo/client';
import { navigate } from 'gatsby';
import { equals, isEmpty } from 'ramda';
import React, { FC, ReactNode, useContext, useEffect } from 'react';
import styled from 'styled-components';

import useMemberInfo from '@hooks/useMemberInfo';

import {
	getFirstStep,
	getLastStep,
	getNextStepForKey,
	getPathForCurrentStep,
	getPathPrefixFromPathname,
	getPreviousStepForKey,
	getStepForKey,
	getStepForPath,
	getStepsForFlow,
} from '@utils/flowHelpers';

import { useAuth } from '@providers/AuthProvider';
import FlowContext, { FlowContextProvider } from '@providers/FlowContextProvider';

import { FlowIdTypes, FlowTypes, FormData } from '@folx-types/flow';
import { FlowStepData, formStepFinder, onboardingFormStepFinder } from '@folx-types/flow-steps';

interface IParentRouteProps {
	flowId: FlowIdTypes | FlowTypes;
	path: string;
	stepKey: string;
	shouldEscapeRoute?: formStepFinder;
	skip?: onboardingFormStepFinder;
	skipTo?: string;
	backTo?: string;
	children?: ReactNode;
	onboardingStep?: boolean;
}

const Wrapper = styled.div`
	display: contents;

	> div[tabindex='-1'] {
		display: contents;
	}
`;

const ParentRoute: FC<IParentRouteProps> = ({
	flowId,
	path,
	children: nestedRouter,
	shouldEscapeRoute = () => {
		return;
	},
	skip = () => {
		return false;
	},
	skipTo,
	backTo,
	stepKey: parentStepKey,
	...props
}) => {
	const {
		props: { children: routes },
	} = nestedRouter;

	const currentPath = getPathForCurrentStep(props['*']);
	const stepsForFlow = getStepsForFlow(routes);
	const firstStep = getFirstStep(stepsForFlow);
	const lastStep = getLastStep(stepsForFlow);
	const currentStep = getStepForPath(currentPath, stepsForFlow) ?? ({} as FlowStepData);

	const context = useContext(FlowContext);
	const {
		existingFormData,
		submitFormData,
		handleFormSubmission: baseFormSubmission,
		handleNavBack: baseNavBack,
		handleNavNext: baseNavNext,
		hideNavBack,
		parentRef,
		destinationFlow,
	} = context;

	const { memberInfo, loading: memberLoading } = useMemberInfo();

	const { loggedIn, initialized: authInitialized } = useAuth();

	useEffect(() => {
		if (authInitialized && !loggedIn) {
			navigate(`/app/flows/${flowId}/onboarding`, { replace: true });
		}

		if (props?.location?.state?.reverse) {
			const pathPrefix = getPathPrefixFromPathname(location.pathname, path);
			const step = shouldEscapeRoute(existingFormData) ? firstStep : lastStep;
			navigate(`/${pathPrefix}/${path}/${step?.path}`);
		}

		if (isEmpty(currentStep)) {
			const pathPrefix = getPathPrefixFromPathname(location.pathname, path);
			navigate(`/${pathPrefix}/${path}/${firstStep?.path}`);
		}
	}, [loggedIn, authInitialized]);

	useEffect(() => {
		if (skip && !memberLoading) {
			const pathPrefix = getPathPrefixFromPathname(location.pathname, path);
			const skipResult = skip({
				formData: existingFormData,
				flowId,
				destinationFlowId: destinationFlow,
				memberInfo: memberInfo,
			});

			if (skipResult) {
				navigate(`/${pathPrefix}/${props?.location?.state?.reverse ? backTo : skipTo}`);
			}
		}
	}, [memberLoading]);

	const handleNavBack = (stepKey: string) => {
		const pathPrefix = getPathPrefixFromPathname(location.pathname, path);
		const previousStep = getPreviousStepForKey(stepsForFlow, stepKey);
		const previousPath = getPathForCurrentStep(previousStep?.path ?? '');

		if (equals(currentStep, firstStep)) {
			return baseNavBack(parentStepKey);
		}

		if (currentStep.returnKey) {
			const returnStep = getStepForKey(stepsForFlow, currentStep.returnKey);
			const returnPath = getPathForCurrentStep(returnStep?.path ?? '');

			navigate(`/${pathPrefix}/${path}/${returnPath}`);
			return;
		}

		navigate(`/${pathPrefix}/${path}/${previousPath}`);
	};

	const handleNavNext = (stepKey: string) => {
		if (equals(currentStep, lastStep)) {
			return baseNavNext(parentStepKey);
		}

		const pathPrefix = getPathPrefixFromPathname(location.pathname, path);
		const nextStep = getNextStepForKey(stepsForFlow, flowId, stepKey, existingFormData);
		const nextPath = getPathForCurrentStep(nextStep?.path ?? '');

		navigate(`/${pathPrefix}/${path}/${nextPath}`);
	};

	const handleFormSubmission = async (data: FormData, stepKey: string) => {
		const combinedData = { ...existingFormData, ...data };
		const pathPrefix = getPathPrefixFromPathname(location.pathname, path);
		const nextStep = getNextStepForKey(stepsForFlow, path, stepKey, combinedData);
		const nextPath = getPathForCurrentStep(nextStep?.path ?? '');

		if (loggedIn && !isEmpty(data)) {
			await submitFormData(data);
		}

		if (shouldEscapeRoute(combinedData)) {
			baseFormSubmission(data, parentStepKey);
			return;
		}

		if (equals(currentStep, lastStep)) {
			baseFormSubmission(data, parentStepKey);
			return;
		}

		const currentStepForKey = getStepForKey(stepsForFlow, stepKey);
		if (currentStepForKey?.finalStep) {
			baseFormSubmission(data, parentStepKey);
			return;
		}

		navigate(`/${pathPrefix}/${path}/${nextPath}`);
	};

	return (
		<FlowContextProvider
			value={{
				destinationFlow: flowId,
				existingFormData,
				flow: path,
				flowId,
				handleFormSubmission,
				handleNavBack,
				handleNavNext,
				submitFormData,
				hideNavBack,
				parentRef,
			}}>
			<Wrapper>{nestedRouter}</Wrapper>
		</FlowContextProvider>
	);
};

export default ParentRoute;
